import { Collapse } from "antd";
import { useState, useEffect, useMemo } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "../icons";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import axios from "axios";
import "../settings.css";
import { mapSettingsResponse } from "../utils/settings";
import { OS1_DevPortal_URL, SYSTEM_APP_ID } from "../utils/constants";
import { getConsoleInstance } from "../App";
import { ExpandIcon } from "../icons/expandIcon";
import { CollapseIcon } from "../icons/collapseIcon";
import { OS1HttpClient } from "@os1-platform/console-ui-react";

const SettingsPage = ({ token }) => {
  const [openedSettingIds, setOpenedSettingIds] = useState([]);
  const [allSettingsIds, setAllSettingIds] = useState([]);
  const [isAllCollapsed, setIsAllCollapsed] = useState(false);
  const [subscriptionSettings, setSubscriptionSettings] = useState(null);
  const consoleInstance = getConsoleInstance();

  useEffect(() => {
    const reqHeaders = {
      withUserInfo: false,
      withAuth: false,
    };
    const authUrl =
      consoleInstance?.authInitializer?.client?._settings?._authority;
    (async () => {
      const tenantId = await consoleInstance.getChildTenantId();
      const subscriptionSettings = await getSubscriptionSettings(
        consoleInstance,
        "GetSubscriptionSettings",
        reqHeaders,
        tenantId
      );
      if (subscriptionSettings?.data?.data) {
        const mappedResponse = mapSettingsResponse(
          subscriptionSettings?.data?.data
        );
        setSubscriptionSettings(mappedResponse);
        const idsData = mappedResponse.map((data) => data.id);
        setOpenedSettingIds(idsData);
        setAllSettingIds(idsData);
      }
    })();
  }, []);

  const getSubscriptionSettings = async (consoleInstance, requestId, reqHeaders, tenantId) => {
    console.log("getSubscriptionSettings->", tenantId, reqHeaders, requestId);
    const axiosClient = new OS1HttpClient(consoleInstance.authInitializer, OS1_DevPortal_URL);
    const resp = await axiosClient.get(`/app/console-api/subscription/${tenantId}/settings`, requestId, reqHeaders);
    // const resp = await axios.get(
    //   OS1_DevPortal_URL + `/app/console-api/subscription/${tenantId}/settings`,
    //   {
    //     headers: {
    //       "X-COREOS-REQUEST-ID": requestId,
    //       "X-COREOS-TID": tenantId,
    //       "x-coreos-access": token,
    //     },
    //   }
    // );
    console.log("getSubscriptionSettings->", resp);
    return resp;
  };

  const handleSubSettingClick = (url) => {
    const currentAppRoutingBehaviour = localStorage.getItem(
      "consoleAppRedirectBehaviour"
    );

    if (currentAppRoutingBehaviour === "Open in a new tab") {
      window.open(url, "_blank");
    } else {
      window.location.href = url;
    }
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const labelJsx = (title, iconSrc) => {
    return (
      <div className="flex flex-row gap-3 items-center">
        <div className="bg-[#e0e7ff] h-10 w-10 flex justify-center items-center flex-shrink-0 rounded-lg">
          <img src={iconSrc} className="h-6 w-6" alt={title} />
        </div>
        <div className="text-xl	font-semibold	text-[#3D445C]">{title}</div>
      </div>
    );
  };

  const childJsx = (subSettings) => {
    return (
      <div className="grid grid-cols-12 py-5 px-4 gap-x-4 gap-y-8">
        {subSettings?.length > 0 ? (
          subSettings.map((setting) => {
            return (
              <div
                key={setting.appDisplayName}
                className="col-span-4 flex items-center gap-4"
              >
                <img
                  src={setting.icon}
                  className="h-6 w-6"
                  alt={setting.appDisplayName}
                />
                <div
                  className="text-lg font-medium text-[#3D445C] hover:text-[#5B80F7] cursor-pointer"
                  onClick={() => handleSubSettingClick(setting.appSettingsUrl)}
                >
                  {setting.appDisplayName}
                </div>
              </div>
            );
          })
        ) : (
          <div className="col-span-12 text-center text-[#3D445C]">
            There are no apps to be configured. Please contact us in case of
            questions.
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!openedSettingIds.length) setIsAllCollapsed(true);
    else if (openedSettingIds.length == allSettingsIds.length)
      setIsAllCollapsed(false);
  }, [JSON.stringify(openedSettingIds), JSON.stringify(allSettingsIds)]);

  return (
    subscriptionSettings && (
      <div
        className={"mx-auto bg-white max-w-[1248px] relative overflow-hidden"}
      >
        <div className="mb-5 flex items-center gap-3">
          <div className="flex items-center justify-between w-full">
            <span className="text-3xl font-bold text-[#3D445C]">Settings</span>
            <div
              onClick={() => {
                if (isAllCollapsed) setOpenedSettingIds(allSettingsIds);
                else setOpenedSettingIds([]);

                setIsAllCollapsed((prev) => !prev);
              }}
              className="flex items-center justify-center gap-2 cursor-pointer"
            >
              {isAllCollapsed ? <ExpandIcon /> : <CollapseIcon />}
              <span className="text-[#5B80F7] font-[500] text-[14px]">
                {isAllCollapsed ? "Expand All" : "Collapse All"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          {subscriptionSettings.map((setting) => {
            return (
              <div
                key={setting.id}
                className="bg-white border border-[#E0E7FF] rounded-lg overflow-hidden"
              >
                <Collapse
                  onChange={() => {
                    console.log("onChangeData->", openedSettingIds);
                    if (openedSettingIds.includes(setting.id)) {
                      const omitThisIdData = openedSettingIds.filter(
                        (item) => item !== setting.id
                      );
                      setOpenedSettingIds(omitThisIdData);
                    } else setOpenedSettingIds((prev) => [...prev, setting.id]);
                  }}
                  activeKey={
                    openedSettingIds.includes(setting.id) ? [setting.id] : []
                  }
                  expandIconPosition="end"
                  expandIcon={({ isActive }) =>
                    isActive ? <ChevronUpIcon /> : <ChevronDownIcon />
                  }
                  collapsible="header"
                  ghost={true}
                  items={[
                    {
                      key: setting.id,
                      label: labelJsx(
                        setting.settingsLabel,
                        setting.settingsIcon
                      ),
                      children: childJsx(setting.appSettings),
                    },
                  ]}
                />
              </div>
            );
          })}
        </div>
      </div>
    )
  );
};

export default SettingsPage;
